import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  MapPin,
  Building2,
  User,
  X,
  Send,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import { requestMeeting, RequestMeetingPayload } from '../../lib/api';

interface RequestMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Current user info (requester)
  currentUser: {
    type: 'attendee' | 'exhibitor';
    id: string;
    name: string;
    org?: string;
    email: string;
    stallNumber?: string;
  };
  // Target user info (invitee)
  targetUser: {
    type: 'attendee' | 'exhibitor';
    id: string;
    name: string;
    org?: string;
    email: string;
    stallNumber?: string;
  };
  initialDateTime?: string; // Optional 'YYYY-MM-DD HH:mm'
}

const EXPO_DATES = [
  { value: '2027-07-27', label: 'Day 1: Tuesday, 27 July 2027' },
  { value: '2027-07-28', label: 'Day 2: Wednesday, 28 July 2027' },
];

const TIME_SLOTS = [
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
];

export function RequestMeetingModal({
  isOpen,
  onClose,
  currentUser,
  targetUser,
  initialDateTime,
}: RequestMeetingModalProps) {
  const queryClient = useQueryClient();

  const [date, setDate] = useState(() => {
    if (initialDateTime) return initialDateTime.split(' ')[0];
    return '2027-07-27';
  });

  const [time, setTime] = useState(() => {
    if (initialDateTime && initialDateTime.includes(' ')) return initialDateTime.split(' ')[1];
    return '11:00';
  });

  // Sync state if initialDateTime changes
  useEffect(() => {
    if (initialDateTime) {
      const parts = initialDateTime.split(' ');
      if (parts[0]) setDate(parts[0]);
      if (parts[1]) setTime(parts[1]);
    }
  }, [initialDateTime]);

  const [title, setTitle] = useState(
    `B2B Discussion: ${currentUser.org || currentUser.name} & ${targetUser.org || targetUser.name}`
  );
  const [agenda, setAgenda] = useState('');

  // Default location: If target is exhibitor, default to their stall; if requester is exhibitor, default to their stall; else Lounge
  const defaultLocType = targetUser.stallNumber || currentUser.stallNumber ? 'booth' : 'lounge';
  const defaultLocDetails = targetUser.stallNumber
    ? `Stall ${targetUser.stallNumber}`
    : currentUser.stallNumber
    ? `Stall ${currentUser.stallNumber}`
    : 'Hall 3 B2B Networking Lounge';

  const [locationType, setLocationType] = useState<'booth' | 'meeting_suite' | 'lounge' | 'cafe'>(defaultLocType);
  const [locationDetails, setLocationDetails] = useState(defaultLocDetails);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Compute 30-min end time
  const computeEndTime = (startDateStr: string, startTimeStr: string): string => {
    const [h, m] = startTimeStr.split(':').map(Number);
    const endMinutes = m + 30;
    const endH = endMinutes >= 60 ? h + 1 : h;
    const endM = endMinutes >= 60 ? endMinutes - 60 : endMinutes;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${startDateStr} ${pad(endH)}:${pad(endM)}`;
  };

  const mutation = useMutation({
    mutationFn: (payload: RequestMeetingPayload) => requestMeeting(payload),
    onSuccess: () => {
      setSuccessMessage('Meeting invitation dispatched successfully! The invitee will receive notification via email.');
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      setTimeout(() => {
        setSuccessMessage(null);
        onClose();
      }, 2000);
    },
    onError: (err: any) => {
      setErrorMessage(err.message || 'Failed to schedule meeting');
    },
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const start = `${date} ${time}`;
    const end = computeEndTime(date, time);

    mutation.mutate({
      requesterType: currentUser.type,
      requesterId: currentUser.id,
      requesterName: currentUser.name,
      requesterOrg: currentUser.org || '',
      requesterEmail: currentUser.email,
      targetType: targetUser.type,
      targetId: targetUser.id,
      targetName: targetUser.name,
      targetOrg: targetUser.org || '',
      targetEmail: targetUser.email,
      start,
      end,
      title: title.trim(),
      agenda: agenda.trim() || undefined,
      locationType,
      locationDetails: locationDetails.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/70">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-[#0A3D27] flex items-center justify-center font-bold">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">Schedule B2B Meeting</h2>
              <p className="text-xs text-slate-500">
                With {targetUser.name} {targetUser.org ? `(${targetUser.org})` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {errorMessage && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2 text-xs text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-xs text-emerald-800">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Date Picker */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              <span>Expo Date</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {EXPO_DATES.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDate(d.value)}
                  className={`px-3 py-2 rounded-xl text-xs font-medium border text-center transition-all cursor-pointer ${
                    date === d.value
                      ? 'border-[#0A3D27] bg-emerald-50/80 text-[#0A3D27] font-bold shadow-2xs'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <span className="block font-bold">{d.label.split(':')[0]}</span>
                  <span className="block text-[10px] text-slate-400">{d.label.split(',')[1]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Time Slot Picker */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <span>30-Minute Time Slot</span>
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5 max-h-32 overflow-y-auto p-1 border border-slate-100 rounded-xl bg-slate-50/50">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => setTime(slot)}
                  className={`py-1.5 px-2 text-[11px] rounded-lg text-center font-mono transition-colors cursor-pointer ${
                    time === slot
                      ? 'bg-[#0A3D27] text-white font-bold'
                      : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400">
              Slot duration: 30 minutes ({date} {time} – {computeEndTime(date, time).split(' ')[1]})
            </p>
          </div>

          {/* Meeting Location */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-500" />
              <span>Meeting Location</span>
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                type="button"
                onClick={() => {
                  setLocationType('booth');
                  setLocationDetails(
                    targetUser.stallNumber
                      ? `Stall ${targetUser.stallNumber}`
                      : currentUser.stallNumber
                      ? `Stall ${currentUser.stallNumber}`
                      : 'Exhibitor Stall'
                  );
                }}
                className={`py-2 px-3 rounded-xl border text-xs text-left transition-all ${
                  locationType === 'booth'
                    ? 'border-[#0A3D27] bg-emerald-50/60 font-bold text-[#0A3D27]'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                🏢 Exhibition Stall
              </button>
              <button
                type="button"
                onClick={() => {
                  setLocationType('lounge');
                  setLocationDetails('Hall 3 Networking Lounge & Cafe');
                }}
                className={`py-2 px-3 rounded-xl border text-xs text-left transition-all ${
                  locationType === 'lounge'
                    ? 'border-[#0A3D27] bg-emerald-50/60 font-bold text-[#0A3D27]'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                ☕ Networking Lounge
              </button>
              <button
                type="button"
                onClick={() => {
                  setLocationType('meeting_suite');
                  setLocationDetails('VIP Meeting Suite MR-1 (Hall 3 Mezzanine)');
                }}
                className={`py-2 px-3 rounded-xl border text-xs text-left transition-all ${
                  locationType === 'meeting_suite'
                    ? 'border-[#0A3D27] bg-emerald-50/60 font-bold text-[#0A3D27]'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                🛋️ Meeting Suite (MR)
              </button>
              <button
                type="button"
                onClick={() => {
                  setLocationType('cafe');
                  setLocationDetails('Ground Floor Business Cafe Table');
                }}
                className={`py-2 px-3 rounded-xl border text-xs text-left transition-all ${
                  locationType === 'cafe'
                    ? 'border-[#0A3D27] bg-emerald-50/60 font-bold text-[#0A3D27]'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                🍽️ Business Cafe
              </button>
            </div>
            <input
              type="text"
              required
              value={locationDetails}
              onChange={(e) => setLocationDetails(e.target.value)}
              placeholder="e.g. Stall A-104 or Lounge Table 4"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
            />
          </div>

          {/* Meeting Title & Agenda */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700">Subject / Discussion Topic</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700">Agenda Note (Optional)</label>
            <textarea
              rows={2}
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
              placeholder="Briefly state what you would like to discuss (e.g., distributorship, bulk pricing, partnership)..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:outline-none focus:border-[#0A3D27] focus:bg-white resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold shadow-md shadow-emerald-950/20 disabled:opacity-50 transition-all cursor-pointer"
            >
              {mutation.isPending ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Dispatching...</span>
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  <span>Send Meeting Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RequestMeetingModal;
